/**
 * Urban Equestrian Design System
 * Premium/Luxury Design Tokens
 *
 * Philosophy: Restraint, clarity, craftsmanship
 */

export const designTokens = {
  // SPACING SCALE (4px base unit)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
    '4xl': '96px',
    '5xl': '128px',
  },

  // BORDER RADIUS
  radius: {
    none: '0px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    full: '9999px',
  },

  // SHADOWS (2-level max for premium feel)
  shadows: {
    subtle: '0 2px 8px rgba(0, 0, 0, 0.08)',
    elevated: '0 8px 16px rgba(0, 0, 0, 0.12)',
    elevatedDark: '0 8px 16px rgba(0, 0, 0, 0.3)',
    none: 'none',
  },

  // TYPOGRAPHY SCALE
  typography: {
    // Font Families
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
      serif: 'Georgia, "Times New Roman", serif',
    },

    // Font Sizes
    fontSize: {
      xs: '12px',
      sm: '13px',
      base: '15px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
      '5xl': '48px',
    },

    // Font Weights
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    // Line Heights
    lineHeight: {
      tight: 1.2,
      snug: 1.4,
      normal: 1.6,
      relaxed: 1.8,
      loose: 2.0,
    },

    // Letter Spacing
    letterSpacing: {
      tight: '-0.5px',
      normal: '0px',
      wide: '0.3px',
      wider: '1px',
    },
  },

  // COLORS (Neutral palette + 1 accent)
  colors: {
    // Light Mode
    light: {
      primary: '#1976d2', // MUI default blue (consistent)
      primaryDark: '#115293',

      background: {
        default: '#FFFFFF',
        subtle: '#F9F9F9',
        elevated: '#F5F5F5',
      },

      text: {
        primary: '#000000',
        secondary: '#555555',
        tertiary: '#999999',
        disabled: '#CCCCCC',
      },

      border: {
        default: '#EEEEEE',
        subtle: '#F5F5F5',
        emphasis: '#CCCCCC',
      },
    },

    // Dark Mode
    dark: {
      primary: '#1976d2', // Same accent for consistency
      primaryDark: '#115293',

      background: {
        default: '#121212',
        subtle: '#1a1a1f',
        elevated: '#232323',
      },

      text: {
        primary: '#FFFFFF',
        secondary: '#CCCCCC',
        tertiary: '#888888',
        disabled: '#555555',
      },

      border: {
        default: '#333333',
        subtle: '#2a2a2a',
        emphasis: '#555555',
      },
    },
  },

  // TRANSITIONS (Smooth, premium easing)
  transitions: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
    },
    easing: {
      default: 'cubic-bezier(0.4, 0, 0.2, 1)',
      in: 'cubic-bezier(0.4, 0, 1, 1)',
      out: 'cubic-bezier(0, 0, 0.2, 1)',
      inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // BREAKPOINTS (Mobile-first)
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },

  // Z-INDEX SCALE
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
} as const;

// Type exports for TypeScript
export type DesignTokens = typeof designTokens;
export type Spacing = keyof typeof designTokens.spacing;
export type Radius = keyof typeof designTokens.radius;
export type Shadow = keyof typeof designTokens.shadows;
